import { createElement, type ComponentType } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';

// Type for component modules returned by import.meta.glob
export type ComponentModule = {
  default?: ComponentType<Record<string, unknown>>;
  [key: string]: ComponentType<Record<string, unknown>> | undefined;
};

// Store React roots for reuse
const reactRoots = new Map<Element, Root>();

/**
 * Gets module paths for a specific Vite entrypoint
 * Used for dynamic imports with import.meta.glob
 * includes all .js, .jsx, .ts, .tsx files except for .test.*, .spec.* and anything in tests/ directories
 */
export function getModulePathsForEntrypoint(entrypoint: string): string[] {
  return [
    `../containers/${entrypoint}/**/*.{js,jsx,ts,tsx}`,
    `!../containers/${entrypoint}/**/tests/**`,
    `!../containers/${entrypoint}/**/mocks/**`,
    `!../containers/${entrypoint}/**/*.test.{js,jsx,ts,tsx}`,
    `!../containers/${entrypoint}/**/*.spec.{js,jsx,ts,tsx}`
  ];
}

/**
 * Build a component registry from imported modules
 * Extracts component names from paths and handles both default and named exports
 */
export function buildComponentRegistry(
  modules: Record<string, ComponentModule>
): Record<string, ComponentType<Record<string, unknown>>> {
  const registry: Record<string, ComponentType<Record<string, unknown>>> = {};

  for (const [path, module] of Object.entries(modules)) {
    // Extract component name from path
    // '../containers/Header/index.js' -> 'Header'
    // '../containers/QuickBill/QuickBillModal/index.js' -> 'QuickBill/QuickBillModal'
    // '../containers/admin/FeatureToggles/index.js' -> 'admin/FeatureToggles'
    const match = path.match(/\.\.\/(containers|components)\/(.+)\.[jt]sx?$/);
    if (!match) continue;

    let componentName = match[2];

    // Remove /index suffix if present
    componentName = componentName.replace(/\/index$/, '');

    // Get the component from default export or first named export
    const component = module.default || Object.values(module).find(Boolean);

    const isRenderable =
      typeof component === 'function' ||
      (typeof component === 'object' &&
        component !== null &&
        '$$typeof' in component);

    if (component && isRenderable && !registry[componentName]) {
      registry[componentName] = component;
    }
  }

  return registry;
}

/**
 * Mount a single React component to an element
 */
export function mountComponent(
  element: Element,
  componentRegistry: Record<string, ComponentType<Record<string, unknown>>>
): void {
  const componentName = element.getAttribute('data-react-class');
  const propsJson = element.getAttribute('data-react-props') || '{}';

  if (!componentName) {
    console.warn(
      'ReactMount: No data-react-class attribute found on element',
      element
    );
    return;
  }

  const Component = componentRegistry[componentName];

  if (!Component) {
    console.error(`ReactMount: Component not found: ${componentName}`);
    console.error('Available components:', Object.keys(componentRegistry));
    return;
  }

  let props: Record<string, unknown>;
  try {
    props = JSON.parse(propsJson);
  } catch (e) {
    console.error(`ReactMount: Failed to parse props for ${componentName}:`, e);
    props = {};
  }

  // Reuse existing root or create new one
  let root = reactRoots.get(element);
  if (!root) {
    root = createRoot(element);
    reactRoots.set(element, root);
  }

  // use flushSync to ensure react renders component synchrnously in root.render, which allows us to guarantee the 'react-component-mounted' event is dispatched after the component is actually mounted in the DOM
  // eslint-disable-next-line @eslint-react/dom-no-flush-sync
  flushSync(() => {
    root.render(createElement(Component, props));
  });
  // emit event when component is mounted
  const event = new CustomEvent('react-component-mounted', {
    detail: { componentName, element }
  });
  document.dispatchEvent(event);
}

/**
 * Mount all React components in a given scope (defaults to document)
 */
export function mountComponents(
  componentRegistry: Record<string, ComponentType<Record<string, unknown>>>,
  scope: Document | Element = document
): void {
  const elements = scope.querySelectorAll('[data-react-class]');
  elements.forEach((element) => {
    try {
      mountComponent(element, componentRegistry);
    } catch (e) {
      window.Rollbar?.error(
        `[ReactMount] Failed to mount ${element.getAttribute('data-react-class')}`,
        e
      );
    }
  });
}

/**
 * Unmount a React component and cleanup
 */
export function unmountComponent(element: Element): void {
  const root = reactRoots.get(element);
  if (root) {
    root.unmount();
    reactRoots.delete(element);
  }
}

/**
 * Unmount all React components in a given scope
 */
export function unmountComponents(scope: Document | Element = document): void {
  const elements = scope.querySelectorAll('[data-react-class]');
  elements.forEach((element) => unmountComponent(element));
}

/**
 * Export utilities to window for use in js.erb files
 */
export function exportToWindow(
  componentRegistry: Record<string, ComponentType<Record<string, unknown>>>
): void {
  window.ReactMount = {
    mountComponents: (scope?: Document | Element) =>
      mountComponents(componentRegistry, scope),
    mountComponent: (element: Element) =>
      mountComponent(element, componentRegistry),
    unmountComponents,
    unmountComponent,
    registry: componentRegistry
  };
}
